import $$ from 'jquery'
import overlay from '@/common/overlay'
import './exoInpage.scss'

/**
 * Admin modals drawn in the page, without the iframe.
 *
 * Pim, 16 Sep 2026: "I see that the AI translate modal responds very fast and
 * is not an Iframe - would it be a good decision to move all our modals to
 * this technology?" The answer was: modal by modal, small ones first. This is
 * the host for that: the same frame box the iframe modals use, but inside it
 * a shadow tree with the admin app, loaded once from /exo_admin/embed/ (see
 * exo_admin/src/embed/main.js) and navigated by route push, exactly as the
 * bridge navigated the iframe.
 *
 * Which routes come here is a list (INPAGE_ROUTES); everything else still
 * goes through exoBridge's iframe. It is how these modals open for everyone
 * (Pim, 13 Oct 2026: "Yes, frameless can be default / We can abandon this
 * parameter I think") - until then a browser had to ask for it once per site
 * with ?exoInpage=1, so on every new address the converted modals were frames
 * again. The server can still switch it off for a page by sending
 * ExologEditorParams.inpage_modals = false. Should the bundle fail to load,
 * exoBridge falls back to the iframe for that open.
 */
const EMBED_SRC = '/exo_admin/embed/exo-admin-embed.js'
// R_CONTROLLER: workflow / page-group settings (16 Sep 2026). R_REACT_EDIT: the
// edit react form, every way the editor opens it - the toolbar's Edit page, Add
// new react, the translation loader, a sidebar ROUTE button (Pim, 17 Sep 2026:
// "next one up is the iframeless version of the edit react modal").
// R_REACT_SEND: the test-mail dialog of a mail page (Pim, same day: "this modal
// can become iframeless as well"). The newsletter button's R_REACT_MAILING
// still opens in the iframe. R_EDITOR_STYLES: the styles picker of a textarea
// (20 Sep 2026). R_TINYMCE_STYLES: the textarea's CSS editor behind the
// paintbrush (Pim, 20 Sep 2026: "Can you make this modal iFrameless as well?").
// R_CONTROLLER_SETTINGS: a group's settings opened straight from the toolbar's
// gear - the view the group controller already shows in the page behind its
// own Settings button (Pim, 13 Oct 2026: "not all modals had this treatment yet").
// R_REACT_LAYOUT: the layout editor (Pim, same day: "The layout modal is still
// in a frame"). R_TIPTAP_BTNSETTINGS: a textarea's toolbar settings behind the
// gear ("The toolbar settings modal is still in a frame").
const INPAGE_ROUTES = [
    'R_CONTROLLER',
    'R_CONTROLLER_SETTINGS',
    'R_REACT_EDIT',
    'R_REACT_SEND',
    'R_REACT_LAYOUT',
    'R_EDITOR_STYLES',
    'R_TINYMCE_STYLES',
    'R_TIPTAP_BTNSETTINGS',
]

const exoInpage = {
    $host: null,
    $inner: null,
    _loading: null,
    _resolve: null,

    enabled() {
        return !(window.ExologEditorParams && window.ExologEditorParams.inpage_modals === false)
    },

    /** Whether this open goes in-page rather than into the iframe. */
    handles(params) {
        return !!(params && params.name && INPAGE_ROUTES.includes(params.name)) && this.enabled()
    },

    isOpen() {
        return !!(this.$host && this.$host[0].isConnected && this.$host.hasClass('frame-box--open'))
    },

    /**
     * Boot the admin into the (still hidden) host ahead of the first click, so
     * an open is a route push and not a cold start. Pim, 17 Sep 2026: "works
     * as well but not as fast as I hoped". Rejects when the bundle cannot
     * come up; open() then tries once more and exoBridge falls back.
     */
    async preload() {
        this._ensureHost()
        try {
            await this._mount()
        } catch (e) {
            // A host keeps its shadow root: a retry needs a fresh one.
            if (!this.isOpen()) this._teardown()
            throw e
        }
    },

    async _mount() {
        await this._load()
        await window.ExoAdminEmbed.mount(this.$inner.find('.exo-inpage__mount')[0], { version: this._version() })
    },

    /**
     * Open the admin at `params` (a router location). Resolves when the modal
     * closes, like exoBridge.open(), so callers keep their `.then(reload)`.
     */
    async open(params) {
        if (this.isOpen()) {
            this.close()
        }
        this._ensureHost()

        const closed = new Promise((resolve) => {
            this._resolve = resolve
        })

        this.$host.addClass('frame-box--open')
        $$('body').addClass('frame-box-parent--open exo-inpage-open')
        overlay.show(this.$inner, { zIndex: '8050' })

        try {
            await this._mount()
            window.ExoAdminEmbed.push(params)
        } catch (e) {
            console.error('[exoInpage] the in-page admin could not open', e)
            this._teardown()
            throw e
        } finally {
            overlay.hide(this.$inner, true)
        }

        return closed
    },

    close() {
        if (!this.$host) return
        this.$host.removeClass('frame-box--open')
        $$('body').removeClass('frame-box-parent--open exo-inpage-open')
        if (window.ExoAdminEmbed && window.ExoAdminEmbed.isInstalled()) {
            window.ExoAdminEmbed.park()
        }
        const resolve = this._resolve
        this._resolve = null
        if (resolve) resolve(this)
    },

    _ensureHost() {
        if (this.$host && this.$host[0].isConnected) return
        // The frame box the iframe modals use, so the two look the same; the
        // iframe is replaced by the shadow host.
        this.$host = $$(
            '<div class="frame-box exo-inpage" role="dialog" tabindex="-1">' +
                '<div class="frame-box__backdrop"></div>' +
                '<div class="frame-box__inner exo-inpage__inner"><div class="exo-inpage__mount"></div></div>' +
            '</div>'
        )
        this.$inner = this.$host.find('.frame-box__inner')
        this.$inner.css({ height: '90%', width: '90%', maxWidth: '1350px' })
        $$('body').append(this.$host)
    },

    _teardown() {
        $$('body').removeClass('frame-box-parent--open exo-inpage-open')
        if (this.$host) {
            this.$host.remove()
        }
        this.$host = null
        this.$inner = null
        const resolve = this._resolve
        this._resolve = null
        if (resolve) resolve(this)
    },

    _version() {
        return (window.ExologEditorParams && window.ExologEditorParams.embed_version) || ''
    },

    _load() {
        if (window.ExoAdminEmbed) return Promise.resolve()
        if (!this._loading) {
            this._loading = new Promise((resolve, reject) => {
                const script = document.createElement('script')
                script.type = 'module'
                script.src = EMBED_SRC + (this._version() ? '?v=' + encodeURIComponent(this._version()) : '')
                script.onload = () => (window.ExoAdminEmbed
                    ? resolve()
                    : reject(new Error('exo-admin-embed.js loaded but did not install')))
                script.onerror = () => reject(new Error('exo-admin-embed.js failed to load'))
                document.head.appendChild(script)
            }).catch((e) => {
                this._loading = null
                throw e
            })
        }
        return this._loading
    },
}

window.exoInpage = exoInpage

export default exoInpage
